/* eslint-disable no-template-curly-in-string */
import moment from 'moment';
import {
  FILE_TYPE_JPEG,
  FILE_TYPE_JPG,
  FILE_TYPE_PDF,
  FILE_TYPE_WORD_DOC,
  FILE_TYPE_WORD_DOCX,
  getFileSizeStr,
  getFileType,
} from './FileUtils';
import { getUnmaskedValue } from './InputMaskUtils';

export const DESCRICAO_REEMBOLSO_MAX_LENGTH = 50;
export const NOME_MAX_LENGTH = 50;
export const NOME_MAE_MAX_LENGTH = 50;
export const NOME_QUALIFICACAO_MAX_LENGTH = 50;
export const EMAIL_MAX_LENGTH = 100;
export const RAZAO_SOCIAL_MAX_LENGTH = 50;
export const NOME_FANTASIA_MAX_LENGTH = 50;
export const ENDERECO_MAX_LENGTH = 50;
export const COMPLEMENTO_MAX_LENGTH = 20;
export const NUMERO_MAX_LENGTH = 20;
export const BAIRRO_MIN_LENGTH = 3;
export const BAIRRO_MAX_LENGTH = 30;
export const RG_LENGTH = 10;
export const ANO_LENGTH = 4;
export const DESCRICAO_LENGTH = 200;
export const ASSUNTO_LENGTH = 200;
export const ATIVIDADES_LENGTH = 2000;
export const REEMBOLSO_LENGTH = 500;
export const INEP_LENGTH = 8;
export const ANALISE_OPERACIONAL_JUSTIFICATIVA_LENGTH = 2000;
export const OBSERVACAO_MAX_LENGTH = 500;
export const DESCRICAO_OUTROS_MAX_LENGTH = 200;
export const REEMBOLSO_DESPESA_FILE_BYTES_LIMIT = 10 * 1024 * 1024;
export const CONTRATO_ENTE_PUBLICO_NAO_ASSINADO_FILE_TYPES = [
  FILE_TYPE_PDF,
  FILE_TYPE_WORD_DOC,
  FILE_TYPE_WORD_DOCX,
];
export const CONTRATO_ENTE_PUBLICO_NAO_ASSINADO_MAX_FILE_SIZE =
  10 * 1024 * 1024;
export const CONTRATO_ENTE_PUBLICO_ASSINADO_FILE_TYPES = [FILE_TYPE_PDF];
export const CONTRATO_ENTE_PUBLICO_ASSINADO_MAX_FILE_SIZE = 10 * 1024 * 1024;
export const CONTRATO_ENTE_PUBLICO_EXTRATO_FILE_TYPES = [
  FILE_TYPE_PDF,
  FILE_TYPE_JPEG,
  FILE_TYPE_JPG,
];
export const CONTRATO_ENTE_PUBLICO_EXTRATO_MAX_FILE_SIZE = 10 * 1024 * 1024;

export const CONTRATO_ENTE_PUBLICO_PLANO_TRABALHO_MAX_FILE_SIZE =
  10 * 1024 * 1024;

export const NUMERO_NAO_INFORMADO = 'S/N';

export const allIsTrue = (validations: boolean[]) => {
  return (
    validations.filter(
      (item: boolean) => typeof item === 'boolean' && item === true
    ).length === validations.length
  );
};

export const minLengthIsValid = (
  value: string,
  minLength: number,
  messageError: string
) => {
  if (typeof value === 'string' && value.trim().length >= minLength) {
    return {
      status: true,
    };
  }
  return {
    status: false,
    error: messageError,
  };
};

export const fileSizeIsValid = (
  size: number,
  maxSize: number,
  messageError: string
) => {
  if (size > maxSize) {
    return {
      status: false,
      error: messageError,
    };
  }
  return {
    status: true,
  };
};

export const fileTypeIsValid = (
  url: string,
  fileTypes: string[],
  messageError: string
) => {
  const fileType = getFileType(url).toLowerCase();
  return fileTypes.indexOf(fileType) > -1
    ? {
      status: true,
    }
    : {
      status: false,
      error: messageError,
    };
};

export const uploadFileValidation = (
  file: { name: string; size: number },
  fileTypes: string[],
  fileSize: number,
  fileTypesError: string,
  fileSizeError?: string
) => {
  let fileSizeValidation = undefined;
  let fileTypeValidation = undefined;

  if (fileTypes && fileTypes.length) {
    const fileTypeMessageError =
      fileTypesError
        ?.split('${1}')
        .join(getFileType(file.name))
        .split('${2}')
        .join((fileTypes ?? []).join(', ')) ?? '-';

    fileTypeValidation = fileTypeIsValid(
      file.name,
      fileTypes,
      fileTypeMessageError
    );
  }

  if (fileSize) {
    const fileSizeMessageError =
      fileSizeError
        ?.split('${1}')
        .join(getFileSizeStr(file.size))
        .split('${2}')
        .join(getFileSizeStr(fileSize)) ?? '-';
    fileSizeValidation = fileSizeIsValid(
      file.size,
      fileSize,
      fileSizeMessageError
    );
  }

  if (fileTypeValidation && !fileTypeValidation.status) {
    return fileTypeValidation;
  }

  if (fileSizeValidation && !fileSizeValidation.status) {
    return fileSizeValidation;
  }

  return {
    status: true,
  };
};

export const requiredIsValid = (
  value: string | number | null,
  messageError: string
) => {
  if (
    (typeof value === 'number' && value !== -1) ||
    (typeof value === 'string' && value.trim().length > 0)
  ) {
    return {
      status: true,
    };
  }
  return {
    status: false,
    error: messageError,
  };
};

export const assessorRequiredIsValid = (value: number, messageError: string) => {
  if (typeof value === 'string' && !isNaN(value)) {
    value = Number(value);
  }
  if (typeof value === 'number' && value >= 0) {
    return {
      status: true,
    };
  }
  return {
    status: false,
    error: messageError,
  };
};

export const tipoInstituicaoRequiredIsValid = (
  value: number | string,
  messageError: string
) => {
  // Verifica se o valor é uma string vazia ou null ou undefined
  if (!value && value !== 0) {
    return {
      status: false,
      error: messageError,
    };
  }

  if (typeof value === 'string' && !isNaN(Number(value))) {
    value = Number(value);
  }

  if (typeof value === 'number' && value > -1) {
    return {
      status: true,
    };
  }

  return {
    status: false,
    error: messageError,
  };
};

export const etapaDaEducacaoRequiredIsValid = (
  value: string[],
  messageError: string
) => {
  if (Array.isArray(value) && value.length > 0) {
    return {
      status: true,
    };
  }
  return {
    status: false,
    error: messageError,
  };
};

export const cooperativaRequiredIsValid = (
  value: number,
  messageError: string
) => {
  if (typeof value === 'string' && !isNaN(value)) {
    value = Number(value);
  }
  if (typeof value === 'number' && value > -1) {
    return {
      status: true,
    };
  }
  return {
    status: false,
    error: messageError,
  };
};

export const periodoIsValid = (value: string, messageError: string) => {
  if (requiredIsValid(value, messageError).status === true) {
    if (value.length === 6) {
      const splittedValue = value.split('-');
      const year = Number(splittedValue[0]);
      const periodo = Number(splittedValue[1]);
      if (year > 1990 && periodo > 0 && periodo < 3) {
        return {
          status: true,
        };
      }
    }
  }
  return {
    status: false,
    error: messageError,
  };
};

const validateNumber = (value: number, messageError: string) => {
  if (typeof value === 'number' && value > -1) {
      return {
          status: true,
      };
  }
  return {
      status: false,
      error: messageError,
  };
};

export const estadoRequiredIsValid = (value: number, messageError: string) => {
  return validateNumber(value, messageError);
};

export const strCidadeRequiredIsValid = (value: string, messageError: string) => {
  return requiredIsValid(value, messageError);
};

export const cidadeRequiredIsValid = (value: number, messageError: string) => {
  return validateNumber(value, messageError);
};

export const maskedValueRequiredIsValid = (
  value: string,
  messageError: string
) => {
  value = getUnmaskedValue(value);
  return requiredIsValid(value, messageError);
};

export const doubleNameIsValid = (value: string, messageError: string) => {
  value = String(value).trim();
  if (
    value.split(' ').length < 2 ||
    value.indexOf('0') > -1 ||
    value.indexOf('1') > -1 ||
    value.indexOf('2') > -1 ||
    value.indexOf('3') > -1 ||
    value.indexOf('4') > -1 ||
    value.indexOf('5') > -1 ||
    value.indexOf('6') > -1 ||
    value.indexOf('7') > -1 ||
    value.indexOf('8') > -1 ||
    value.indexOf('9') > -1
  ) {
    return {
      status: false,
      error: messageError,
    };
  }
  return {
    status: true,
  };
};

export const cepIsValid = (value: string, messageError: string) => {
  value = getUnmaskedValue(value);
  if (typeof value === 'string' && value.trim().length === 8) {
    return {
      status: true,
    };
  }
  return {
    status: false,
    error: messageError,
  };
};

export const celularIsValid = (value: string, messageError: string) => {
  value = getUnmaskedValue(value);
  if (typeof value === 'string' && value.trim().length >= 11) {
    return {
      status: true,
    };
  }
  return {
    status: false,
    error: messageError,
  };
};

export const passwordIsValid = (value: string, messageError: string) => {
  /* eslint-disable-next-line */
  const strongRegex = new RegExp(
    '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})'
  );
  if (strongRegex.test(value)) {
    return {
      status: true,
    };
  }
  return {
    status: false,
    error: messageError,
  };
};

export const equalIsValid = (
  value1: string,
  value2: string,
  messageError: string
) => {
  if (value1 === value2) {
    return {
      status: true,
    };
  }
  return {
    status: false,
    error: messageError,
  };
};

export const telefoneIsValid = (value: string, messageError: string) => {
  value = getUnmaskedValue(value);
  if (typeof value === 'string' && value.trim().length >= 10) {
    return {
      status: true,
    };
  }
  return {
    status: false,
    error: messageError,
  };
};

export const emailIsValid = (value: string, messageError: string) => {
  const usuario = value.substring(0, value.indexOf('@'));
  const dominio = value.substring(value.indexOf('@') + 1, value.length);

  if (
    usuario.length >= 1 &&
    dominio.length >= 3 &&
    usuario.search('@') === -1 &&
    dominio.search('@') === -1 &&
    usuario.search(' ') === -1 &&
    dominio.search(' ') === -1 &&
    dominio.search('.') !== -1 &&
    dominio.indexOf('.') >= 1 &&
    dominio.lastIndexOf('.') < dominio.length - 1
  ) {
    return {
      status: true,
    };
  }
  return {
    status: false,
    error: messageError,
  };
};

export const rgIsValid = (value: string | null, messageError: string) => {
  if (typeof value !== 'string' || value.length < RG_LENGTH) {
    return {
      status: false,
      error: messageError,
    };
  }
  return {
    status: true,
  };
};

const getFalseReturn = (messageError: string) => {
  return {
    status: false,
    error: messageError,
  };
};

export const cnpjIsValid = (value: string, messageError: string) => {
  let cnpj = getUnmaskedValue(value);
  cnpj = cnpj.replace(/[^\d]+/g, '');

  if (cnpj === '') return getFalseReturn(messageError);

  if (cnpj.length !== 14) return getFalseReturn(messageError);

  // Elimina CNPJs inválidos conhecidos
  if (
    cnpj === '00000000000000' ||
    cnpj === '11111111111111' ||
    cnpj === '22222222222222' ||
    cnpj === '33333333333333' ||
    cnpj === '44444444444444' ||
    cnpj === '55555555555555' ||
    cnpj === '66666666666666' ||
    cnpj === '77777777777777' ||
    cnpj === '88888888888888' ||
    cnpj === '99999999999999'
  )
    return getFalseReturn(messageError);

  // Valida DVs
  let tamanho = cnpj.length - 2;
  let numeros = cnpj.substring(0, tamanho);
  const digitos = cnpj.substring(tamanho);
  let soma = 0;
  let pos = tamanho - 7;
  let i;
  let resultado;
  for (i = tamanho; i >= 1; i--) {
    soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
  if (String(resultado) !== digitos.charAt(0)) return getFalseReturn(messageError);
  tamanho = tamanho + 1;
  numeros = cnpj.substring(0, tamanho);
  soma = 0;
  pos = tamanho - 7;
  for (i = tamanho; i >= 1; i--) {
    soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
  if (String(resultado) !== digitos.charAt(1)) return getFalseReturn(messageError);
  return {
    status: true,
  };
};

export const cpfIsValid = (value: string, messageError: string) => {
  const cpf = getUnmaskedValue(value);
  let add = 0;
  let i;
  let rev;
  if (cpf.length < 10) {
    return getFalseReturn(messageError);
  }
  if (
    cpf.length !== 11 ||
    cpf === '00000000000' ||
    cpf === '11111111111' ||
    cpf === '22222222222' ||
    cpf === '33333333333' ||
    cpf === '44444444444' ||
    cpf === '55555555555' ||
    cpf === '66666666666' ||
    cpf === '77777777777' ||
    cpf === '88888888888' ||
    cpf === '99999999999'
  )
    return getFalseReturn(messageError);
  for (i = 0; i < 9; i++) add += parseInt(cpf.charAt(i)) * (10 - i);
  rev = 11 - (add % 11);
  if (rev === 10 || rev === 11) rev = 0;
  if (rev !== parseInt(cpf.charAt(9))) return getFalseReturn(messageError);
  add = 0;
  for (i = 0; i < 10; i++) add += parseInt(cpf.charAt(i)) * (11 - i);
  rev = 11 - (add % 11);
  if (rev === 10 || rev === 11) rev = 0;
  if (rev !== parseInt(cpf.charAt(10))) return getFalseReturn(messageError);
  return {
    status: true,
  };
};

export const hourIsValid = (value: string, messageError?: string) => {
  const unmaskedValue = getUnmaskedValue(value);
  return unmaskedValue.length === 4 && moment(unmaskedValue, 'HHmm').isValid()
    ? {
      status: true,
    }
    : {
      status: false,
      error: messageError,
    };
};

export const dateIsValid = (
  value: string,
  messageError: string | undefined
) => {
  const unmaskedValue = getUnmaskedValue(value);
  return unmaskedValue.length === 8 &&
    moment(unmaskedValue, 'DDMMYYYY').isValid()
    ? {
      status: true,
    }
    : {
      status: false,
      error: messageError,
    };
};

export const inepIsValid = (value: string, messageError: string) => {
  if (isNaN(Number(value.trim()))) {
    return getFalseReturn(messageError);
  }

  if (typeof value === 'string' && value.trim().length === INEP_LENGTH) {
    return {
      status: true,
    };
  } else {
    return getFalseReturn(messageError);
  }
};

export const numeroIsValid = (
  value: string,
  formatMessageError: string,
  messageError: string
) => {
  if (typeof value === 'string' && value.trim() === NUMERO_NAO_INFORMADO) {
    return {
      status: true,
    };
  }

  if (isNaN(Number(value.trim()))) {
    return {
      status: false,
      error: formatMessageError,
    };
  }

  if (typeof value === 'string' && value.trim().length <= NUMERO_MAX_LENGTH) {
    return {
      status: true,
    };
  } else {
    return {
      status: false,
      error: messageError,
    };
  }
};

export const yearIsValid = (value: string, messageError: string) => {
  if (isNaN(Number(value.trim()))) {
    return getFalseReturn(messageError);
  }

  if (typeof value === 'string' && value.trim().length === ANO_LENGTH) {
    return {
      status: true,
    };
  } else {
    return getFalseReturn(messageError);
  }
};

export const integerIsValid = (
  value: string,
  onlyPositive: boolean,
  messageError: string
) => {
  if (
    (typeof value === 'string' && !isNaN(Number(value))) ||
    typeof value === 'number'
  ) {
    if (String(value) === String(parseInt(value))) {
      if (onlyPositive) {
        if (parseInt(value) > 0) {
          return {
            status: true,
          };
        }
        return {
          status: false,
          error: messageError,
        };
      }
      return {
        status: true,
      };
    }
    return {
      status: false,
      error: messageError,
    };
  }
  return {
    status: false,
    error: messageError,
  };
};

export const valorHoraIsValid = (value: string, messageError: string) => {
  const sanitizedValue = String(value)
    .replace('R$ ', '')
    .replace(/\./g, '')
    .replace(',', '.');
  const newValue = Number(sanitizedValue);

  if (!isNaN(newValue)) {
    if (parseFloat(String(newValue)) >= 0) {
      return {
        status: true,
      };
    }
    return {
      status: false,
      error: messageError,
    };
  }
  return {
    status: false,
    error: messageError,
  };
};

export const averageIsValid = (value: string, messageError: string) => {
  if (!isNaN(Number(value))) {
    if (parseFloat(value) >= 0 && parseFloat(value) <= 10) {
      return {
        status: true,
      };
    }
    return {
      status: false,
      error: messageError,
    };
  }
  return {
    status: false,
    error: messageError,
  };
};

export const timePeriodAreValid = (
  horaInicial: string,
  horaFinal: string,
  messageError: string
) => {
  const timeFormat = 'HH:mm';

  if (!hourIsValid(horaInicial) || !hourIsValid(horaFinal)) {
    return getFalseReturn(messageError);
  }
  const momentInicial = new Date(moment(horaInicial, timeFormat).toDate());
  const momentFinal = new Date(moment(horaFinal, timeFormat).toDate());
  if (momentInicial >= momentFinal) {
    return getFalseReturn(messageError);
  }
  return {
    status: true,
  };
};

export const periodIsValid = (
  valueInicial: string,
  valueFinal: string,
  messageError: string
) => {
  const date1IsValid = dateIsValid(valueInicial, messageError);
  const date2IsValid = dateIsValid(valueFinal, messageError);

  if (!date1IsValid.status) {
    return date1IsValid;
  }

  if (!date2IsValid.status) {
    return date2IsValid;
  }

  valueInicial = getUnmaskedValue(valueInicial);
  valueFinal = getUnmaskedValue(valueFinal);
  const dayInicial = String(valueInicial).substring(0, 2);
  const monthInicial = String(valueInicial).substring(2, 4);
  const yearInicial = String(valueInicial).substring(4, 8);
  const dateInicial = new Date(
    parseInt(yearInicial),
    Number(monthInicial) - 1,
    parseInt(dayInicial)
  );

  const dayFinal = String(valueFinal).substring(0, 2);
  const monthFinal = String(valueFinal).substring(2, 4);
  const yearFinal = String(valueFinal).substring(4, 8);
  const dateFinal = new Date(
    parseInt(yearFinal),
    Number(monthFinal) - 1,
    parseInt(dayFinal)
  );

  if (dateInicial > dateFinal) {
    return getFalseReturn(messageError);
  }

  return {
    status: true,
  };
};

export const DEFAULT_VALIDATION_MODEL = (error = false, helperText = '') => {
  return {
    error,
    helperText,
  };
};

export type ValidationType = {
  error: boolean;
  helperText?: string;
};

export const DEFAULT_VALID = DEFAULT_VALIDATION_MODEL(false, '');

export const hasAnyValue = (value: unknown) => {
  if (value === undefined || value === null) {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'number') {
    return value !== undefined && value !== null;
  }

  return Boolean(value);
};
